import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import {
  OpeningHour,
  OpeningHourTypeKind,
  OPENING_HOUR_TYPES,
  StoreOpeningHourSchema,
  WEEKDAYS,
} from '../../../../../shared/models/catalog/opening-hour.model';
import { OpeningHourService } from '../../../../../shared/services/catalog/opening-hour.service';
import { applyZodErrors } from '../../../../../shared/util/zod-form-errors';

interface RowFormValue {
  type: OpeningHourTypeKind;
  date_start: Date | null;
  date_end: Date | null;
  time_start: Date | null;
  time_end: Date | null;
  // per-weekday flat keys
  time_start_mon: Date | null;
  time_end_mon: Date | null;
  time_start_tue: Date | null;
  time_end_tue: Date | null;
  time_start_wed: Date | null;
  time_end_wed: Date | null;
  time_start_thu: Date | null;
  time_end_thu: Date | null;
  time_start_fri: Date | null;
  time_end_fri: Date | null;
  time_start_sat: Date | null;
  time_end_sat: Date | null;
  time_start_sun: Date | null;
  time_end_sun: Date | null;
  notes: string | null;
}

@Component({
  selector: 'app-opening-hour-row-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DatePipe,
    ReactiveFormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    TextareaModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './opening-hour-row-table.component.html',
  styleUrl: './opening-hour-row-table.component.scss',
})
export class OpeningHourRowTableComponent {
  #service = inject(OpeningHourService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #fb = inject(FormBuilder);

  typeId = input.required<number>();
  hours = input.required<OpeningHour[]>();
  changed = output<void>();

  readonly weekdays = WEEKDAYS;
  readonly typeOptions = OPENING_HOUR_TYPES.map((t) => ({
    value: t,
    label: `catalog.openingHours.types.${t}`,
  }));

  dialogVisible = signal(false);
  editing = signal<OpeningHour | null>(null);
  submitting = signal(false);

  form: FormGroup = this.#fb.group({
    type: ['weekdays' as OpeningHourTypeKind, [Validators.required]],
    date_start: [null as Date | null, [Validators.required]],
    date_end: [null as Date | null],
    time_start: [null as Date | null],
    time_end: [null as Date | null],
    time_start_mon: [null as Date | null],
    time_end_mon: [null as Date | null],
    time_start_tue: [null as Date | null],
    time_end_tue: [null as Date | null],
    time_start_wed: [null as Date | null],
    time_end_wed: [null as Date | null],
    time_start_thu: [null as Date | null],
    time_end_thu: [null as Date | null],
    time_start_fri: [null as Date | null],
    time_end_fri: [null as Date | null],
    time_start_sat: [null as Date | null],
    time_end_sat: [null as Date | null],
    time_start_sun: [null as Date | null],
    time_end_sun: [null as Date | null],
    notes: [null as string | null],
  });

  currentType = signal<OpeningHourTypeKind>('weekdays');

  showWeekdays = computed(() => this.currentType() === 'weekdays');
  showRangeTimes = computed(() => this.currentType() === 'changed_times');

  sortedHours = computed<OpeningHour[]>(() =>
    [...this.hours()].sort(
      (a, b) => Date.parse(a.date_start) - Date.parse(b.date_start),
    ),
  );

  openCreateDialog() {
    this.editing.set(null);
    this.form.reset({
      type: 'weekdays',
      date_start: new Date(),
      date_end: null,
      time_start: null,
      time_end: null,
      time_start_mon: null,
      time_end_mon: null,
      time_start_tue: null,
      time_end_tue: null,
      time_start_wed: null,
      time_end_wed: null,
      time_start_thu: null,
      time_end_thu: null,
      time_start_fri: null,
      time_end_fri: null,
      time_start_sat: null,
      time_end_sat: null,
      time_start_sun: null,
      time_end_sun: null,
      notes: null,
    });
    this.currentType.set('weekdays');
    this.dialogVisible.set(true);
  }

  openEditDialog(hour: OpeningHour) {
    this.editing.set(hour);
    this.form.patchValue({
      type: hour.type as OpeningHourTypeKind,
      date_start: hour.date_start ? new Date(hour.date_start) : null,
      date_end: hour.date_end ? new Date(hour.date_end) : null,
      time_start: this.#parseTimeToDate(hour.time_start),
      time_end: this.#parseTimeToDate(hour.time_end),
      time_start_mon: this.#parseTimeToDate(hour.time_start_mon),
      time_end_mon: this.#parseTimeToDate(hour.time_end_mon),
      time_start_tue: this.#parseTimeToDate(hour.time_start_tue),
      time_end_tue: this.#parseTimeToDate(hour.time_end_tue),
      time_start_wed: this.#parseTimeToDate(hour.time_start_wed),
      time_end_wed: this.#parseTimeToDate(hour.time_end_wed),
      time_start_thu: this.#parseTimeToDate(hour.time_start_thu),
      time_end_thu: this.#parseTimeToDate(hour.time_end_thu),
      time_start_fri: this.#parseTimeToDate(hour.time_start_fri),
      time_end_fri: this.#parseTimeToDate(hour.time_end_fri),
      time_start_sat: this.#parseTimeToDate(hour.time_start_sat),
      time_end_sat: this.#parseTimeToDate(hour.time_end_sat),
      time_start_sun: this.#parseTimeToDate(hour.time_start_sun),
      time_end_sun: this.#parseTimeToDate(hour.time_end_sun),
      notes: hour.notes ?? null,
    });
    this.currentType.set(hour.type as OpeningHourTypeKind);
    this.dialogVisible.set(true);
  }

  onTypeChange(value: OpeningHourTypeKind) {
    this.currentType.set(value);
  }

  closeDialog() {
    this.dialogVisible.set(false);
    this.editing.set(null);
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue() as RowFormValue;
    const payload = {
      type: raw.type,
      date_start: this.#toIsoDate(raw.date_start),
      date_end: this.#toIsoDateOrNull(raw.date_end),
      time_start: this.#formatTimeFromDate(raw.time_start),
      time_end: this.#formatTimeFromDate(raw.time_end),
      time_start_mon: this.#formatTimeFromDate(raw.time_start_mon),
      time_end_mon: this.#formatTimeFromDate(raw.time_end_mon),
      time_start_tue: this.#formatTimeFromDate(raw.time_start_tue),
      time_end_tue: this.#formatTimeFromDate(raw.time_end_tue),
      time_start_wed: this.#formatTimeFromDate(raw.time_start_wed),
      time_end_wed: this.#formatTimeFromDate(raw.time_end_wed),
      time_start_thu: this.#formatTimeFromDate(raw.time_start_thu),
      time_end_thu: this.#formatTimeFromDate(raw.time_end_thu),
      time_start_fri: this.#formatTimeFromDate(raw.time_start_fri),
      time_end_fri: this.#formatTimeFromDate(raw.time_end_fri),
      time_start_sat: this.#formatTimeFromDate(raw.time_start_sat),
      time_end_sat: this.#formatTimeFromDate(raw.time_end_sat),
      time_start_sun: this.#formatTimeFromDate(raw.time_start_sun),
      time_end_sun: this.#formatTimeFromDate(raw.time_end_sun),
      notes: this.#nullable(raw.notes),
    };

    const parsed = StoreOpeningHourSchema.safeParse(payload);
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    const editing = this.editing();
    const call$ = editing
      ? this.#service.update(editing.id, parsed.data)
      : this.#service.createForType(this.typeId(), parsed.data);

    call$.subscribe({
      next: () => {
        this.submitting.set(false);
        this.dialogVisible.set(false);
        this.editing.set(null);
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.openingHours.updated'
              : 'catalog.openingHours.created',
          ),
        });
        this.changed.emit();
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.openingHours.updateError'
              : 'catalog.openingHours.createError',
          ),
        });
      },
    });
  }

  onRowDelete(event: Event, hour: OpeningHour) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'catalog.openingHours.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'catalog.openingHours.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.#service.delete(hour.id).subscribe({
          next: () => {
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant('catalog.openingHours.deleted'),
            });
            this.changed.emit();
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant('catalog.openingHours.deleteError'),
            });
          },
        });
      },
    });
  }

  #nullable(v: string | null | undefined): string | null {
    if (v === null || v === undefined) return null;
    const trimmed = String(v).trim();
    return trimmed.length === 0 ? null : trimmed;
  }

  #toIsoDate(value: Date | null): string {
    if (!value) return '';
    return value.toISOString().slice(0, 10);
  }

  #toIsoDateOrNull(value: Date | null): string | null {
    return value ? value.toISOString().slice(0, 10) : null;
  }

  /**
   * Convert a persisted `'HH:MM'` string to a Date suitable for binding to
   * `<p-datepicker [timeOnly]="true">`. The date portion is a fixed sentinel;
   * only hours/minutes are read back via `formatTimeFromDate`.
   */
  #parseTimeToDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const match = /^(\d{1,2}):(\d{2})/.exec(value);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d;
  }

  /** Format a time-only Date into the `'HH:MM'` shape the API expects. */
  #formatTimeFromDate(value: Date | null): string | null {
    if (!value) return null;
    const hh = String(value.getHours()).padStart(2, '0');
    const mm = String(value.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const control = this.form.get(key);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
